import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, Phone, ArrowRight, ShieldCheck, Star, BadgeCheck, Plane } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal, MaskedLines, Overline } from "../components/Motion";
import { RoofOptions, Process, EstimateSection, Marquee, WhyUs } from "../components/Sections";
import { BIZ, SERVICES, STORM } from "../data/site";
import { BeforeAfter } from "../components/BeforeAfter";
import { Seo, faqLd } from "../components/Seo";
import { TOWNS, KEYWORDS } from "../data/areas";

const SEO = {
  roofing: ["Roofer Near Me | Roof Replacement & Repair Milwaukee, WI", "GAF Certified roofer in Milwaukee & Waukesha County: roof replacement, roof repair, hail & storm damage, free drone roof inspection. Call 414-396-7510."],
  siding: ["Siding Contractor Near Me | Siding Installation Milwaukee, WI", "Vinyl & engineered wood siding installation, replacement and repair in Milwaukee & Waukesha County. Soffit & fascia too. Free estimate: 414-396-7510."],
  gutters: ["Gutters Near Me | Seamless Gutters & Gutter Guards Milwaukee", "Seamless gutter installation, gutter guards, downspouts and gutter cleaning in Milwaukee & Waukesha County. BBB A rated. Free estimate: 414-396-7510."],
  "storm-damage": ["Hail & Storm Damage Roof Repair Milwaukee, WI | Free Inspection", "Hail or wind damage? Free roof inspection with drone photos, insurance claim help and storm damage roof repair across Milwaukee & Waukesha County. 414-396-7510."],
};

const TownLinks = ({ s }) => (
  <section className="bg-white pt-20" data-testid="service-town-links">
    <div className="wrap">
      <h2 className="font-display text-3xl font-black uppercase text-slate-900 md:text-4xl">{KEYWORDS[s.slug].terms[0]} — find your town</h2>
      <div className="mt-6 flex flex-wrap gap-2">
        {TOWNS.map((t) => (
          <Link key={t.slug} to={`/${s.slug}/${t.slug}`} className="rounded-full border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:border-slate-900" data-testid={`service-town-${t.slug}`}>{s.title} in {t.name}</Link>
        ))}
      </div>
    </div>
  </section>
);

const HERO_TRUST = [
  { i: ShieldCheck, t: "BBB A rating" }, { i: Star, t: "5.0 on Google" }, { i: BadgeCheck, t: "GAF Certified™" }, { i: Plane, t: "Free drone inspection" },
];

export const PageHero = ({ img, kicker, lines, sub, testid, crumb }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  return (
    <section ref={ref} className="dark-ctx relative overflow-hidden bg-slate-950 text-white" data-testid={testid}>
      <motion.img style={{ y }} src={img} alt="" className="kenburns absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/10" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-950/80 to-transparent" />
      <div className="wrap relative flex min-h-[520px] flex-col justify-end pb-10 pt-16 md:min-h-[600px]">
        <motion.nav initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-300" data-testid="breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link><span>/</span><span className="text-white">{crumb || kicker}</span>
        </motion.nav>
        <Overline light>{kicker}</Overline>
        <h1 className="font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-7xl"><MaskedLines lines={lines} /></h1>
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="mt-5 max-w-md text-sm text-slate-200 md:text-base">{sub}</motion.p>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }} className="mt-8 flex flex-wrap gap-3">
          <Link to="/get-quote" className="btn-amber" data-testid="page-hero-estimate-button">Free quote <ArrowRight className="h-4 w-4" /></Link>
          <a href={BIZ.tel} className="btn-ghost-light" data-testid="page-hero-call-button"><Phone className="h-4 w-4" />{BIZ.phone}</a>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95 }} className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/15 bg-white/10 backdrop-blur-xl md:grid-cols-4" data-testid="page-hero-trust">
          {HERO_TRUST.map((h) => (
            <div key={h.t} className="flex items-center gap-3 bg-slate-950/30 px-4 py-4 text-sm font-semibold"><h.i className="h-5 w-5 shrink-0 text-slate-300" />{h.t}</div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default function ServicePage({ slug }) {
  const s = [...SERVICES, STORM].find((x) => x.slug === slug);
  const others = SERVICES.filter((x) => x.slug !== slug);
  return (
    <main data-testid={`${slug}-page`}>
      <Seo title={SEO[slug][0]} description={SEO[slug][1]} path={`/${slug}`} jsonld={{ "@context": "https://schema.org", ...faqLd(s.faqs) }} />
      <PageHero img={s.img} crumb={s.title} kicker={`Wisconsin ${s.title}`} lines={[`${s.h1 ?? s.title}`, <span key="x" className="text-amber-400">done right.</span>]} sub={s.blurb} testid={`${slug}-hero`} />
      <section className="bg-white py-24 md:py-32">
        <div className="wrap grid items-start gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal><Overline>{s.kicker}</Overline>
              <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900 md:text-6xl">{s.title} for Wisconsin homes</h2>
              <p className="mt-6 text-slate-600">{s.intro}</p></Reveal>
            <ul className="mt-10 grid gap-3 sm:grid-cols-2">
              {s.offerings.map((o, i) => (
                <Reveal key={o} delay={i * 0.05}>
                  <li className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 font-semibold text-slate-800" data-testid={`offering-${i}`}>
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-slate-900"><Check className="h-4 w-4 text-white" /></span>{o}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal className="lg:col-span-6" delay={0.1}>
            <div className="clip-frame overflow-hidden rounded-2xl"><img loading="lazy" decoding="async" src={s.detail} alt={`${s.title} detail`} className="aspect-[4/5] w-full object-cover" /></div>
          </Reveal>
        </div>
      </section>
      <Marquee items={[s.title, "Free Estimates", "Wisconsin", "Done Right", BIZ.phone]} />
      <WhyUs img={s.why} />
      {(slug === "roofing" || slug === "storm-damage") && <BeforeAfter />}
      {slug === "roofing" && <RoofOptions />}
      <Process />
      <section className="bg-slate-50 py-24 md:py-32" data-testid="faq-section">
        <div className="wrap grid gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-4"><Overline>Questions</Overline><h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-slate-900">{s.title} FAQ</h2></Reveal>
          <Accordion type="single" collapsible className="lg:col-span-8">
            {s.faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`q${i}`} className="border-slate-300">
                <AccordionTrigger className="font-display py-6 text-left text-xl font-bold uppercase text-slate-900 hover:no-underline" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-base text-slate-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <TownLinks s={s} />
      <section className="bg-white py-20">
        <div className="wrap grid gap-5 md:grid-cols-2">
          {others.map((o) => (
            <Link key={o.slug} to={`/${o.slug}`} className="group relative block h-56 overflow-hidden rounded-2xl" data-testid={`related-${o.slug}`}>
              <img loading="lazy" decoding="async" src={o.img} alt={o.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-slate-950/60" />
              <div className="relative flex h-full items-end justify-between p-6 text-white"><span className="font-display text-4xl font-black uppercase">{o.title}</span><ArrowRight className="h-7 w-7 text-amber-400 transition-transform group-hover:translate-x-2" /></div>
            </Link>
          ))}
        </div>
      </section>
      <EstimateSection service={s.formService ?? s.title} title={[`Get your ${s.title.toLowerCase()}`, "estimate free"]} />
    </main>
  );
}
